import { ApiResponse } from '@/lib/api/request'
import { useCallback, useEffect, useRef, useState } from 'react'

export interface UsePaginatedPageOptions<T, F extends Record<string, any>> {
    fetchFn: (params: F & { page: number; limit: number }) => Promise<ApiResponse<T[]>>
    filters: F
    limit?: number
    enabled?: boolean
    initialPage?: number
    page?: number
    onPageChange?: (page: number) => void
}

export interface UsePaginatedPageReturn<T> {
    items: T[]
    loading: boolean
    page: number
    limit: number
    total: number
    setPage: (page: number) => void
    reload: () => void
    // Refaz o fetch sem ligar `loading` (evita o spinner/reload da lista em
    // atualizações de socket). Use em handlers de socket; `reload` (com spinner)
    // segue para ações explícitas do usuário.
    reloadSilent: () => void
}

export function usePaginatedPage<T, F extends Record<string, any>>({
    fetchFn,
    filters,
    limit = 15,
    enabled = true,
    initialPage = 1,
    page: controlledPage,
    onPageChange,
}: UsePaginatedPageOptions<T, F>): UsePaginatedPageReturn<T> {
    const isControlled = controlledPage !== undefined && onPageChange !== undefined
    const [items, setItems] = useState<T[]>([])
    const [loading, setLoading] = useState(true)
    const [total, setTotal] = useState(0)
    const [internalPage, setPageState] = useState(initialPage)
    const page = isControlled ? (controlledPage as number) : internalPage
    const fetchingRef = useRef(false)
    const filtersKey = JSON.stringify(filters)
    const previousFiltersKeyRef = useRef<string | null>(null)

    const fetchPage = useCallback(async (targetPage: number, silent = false) => {
        if (fetchingRef.current) return
        fetchingRef.current = true
        if (!silent) setLoading(true)

        try {
            const params = { ...filters, page: targetPage, limit } as F & { page: number; limit: number }
            const response = await fetchFn(params)

            if (response.success) {
                setItems(response.result || [])
                setTotal(response.total ?? 0)
            }
        } finally {
            if (!silent) setLoading(false)
            fetchingRef.current = false
        }
    }, [fetchFn, filters, limit])

    useEffect(() => {
        if (!enabled) {
            setLoading(false)
            return
        }
        const isFirstEnabledRun = previousFiltersKeyRef.current === null
        const filtersChanged = previousFiltersKeyRef.current !== filtersKey
        previousFiltersKeyRef.current = filtersKey
        if (isFirstEnabledRun || !filtersChanged) return
        if (isControlled) {
            if (controlledPage !== 1) onPageChange!(1)
        } else {
            setPageState(1)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, filtersKey])

    useEffect(() => {
        if (!enabled) return
        fetchPage(page)
    }, [enabled, page, filtersKey, fetchPage])

    const setPage = useCallback((next: number) => {
        if (isControlled) {
            if (controlledPage !== next) onPageChange!(next)
        } else {
            setPageState(prev => (prev === next ? prev : next))
        }
    }, [isControlled, controlledPage, onPageChange])

    const reload = useCallback(() => {
        fetchPage(page, false)
    }, [fetchPage, page])

    const reloadSilent = useCallback(() => {
        fetchPage(page, true)
    }, [fetchPage, page])

    return { items, loading, page, limit, total, setPage, reload, reloadSilent }
}
